import { Amplify } from 'aws-amplify';
import amplifyConfig from '../../../amplifyconfiguration.json';
import { BackendModule } from '../types';
import { BackendConfig, AWSConfig } from '../../config/BackendConfig';
import { CognitoAuthService } from '../../adapters/aws/auth/CognitoAuthService';
import { S3StorageService } from '../../adapters/aws/storage/S3StorageService';
import { SNSEventService } from '../../adapters/aws/events/SNSEventService';
import {
  DataClientService,
  GraphQLClientConfig,
} from '../../services/data/DataClientService';
import { AppSyncWebSocketClient } from '@stores/gqlModel/AppSyncWebSocketClient';
import {GraphQLWSClient} from "@stores/gqlModel/GraphQLWSClient";
import {getCurrentUserIdToken} from "../../../utils/user";

let hasBootstrapped = false;

function ensureAwsConfig(config: BackendConfig): AWSConfig {
  if (config.type !== 'aws') {
    throw new Error(`AWS backend received unsupported config type: ${config.type}`);
  }
  return config;
}

const awsBackend: BackendModule = {
  type: 'aws',
  async bootstrap(config) {
    if (hasBootstrapped) {
      return;
    }
    const awsConfig = ensureAwsConfig(config);
    
    const amplifyConfigOverride = {
      ...amplifyConfig,
      oauth: {
        ...amplifyConfig.oauth,
        redirectSignIn: import.meta.env.VITE_BASE_URL,
        redirectSignOut: import.meta.env.VITE_BASE_URL,
      },
    };
    
    Amplify.configure(amplifyConfigOverride);
    
    hasBootstrapped = true;
  },
  createAuthService(config) {
    const awsConfig = ensureAwsConfig(config);
    return new CognitoAuthService(awsConfig.auth);
  },
  createDataClientService(config, authService) {
    const awsConfig = ensureAwsConfig(config);

    const clients: GraphQLClientConfig[] = [
      {
        key: 'main',
        httpEndpoint: awsConfig.data.graphqlEndpoint,
        wsClientFactory: async () => {
          const wsUrl = convertHttpToWss(awsConfig.data.graphqlEndpoint);
          const currentUser = await authService.getCurrentUser();
          const tenantId = currentUser?.['custom:clientId'] || '';
          const wsClient = new AppSyncWebSocketClient(wsUrl, new URL(awsConfig.data.graphqlEndpoint).host, authService, tenantId);
          await wsClient.start();
          return wsClient;
        },
      },
    ];

    const quizHttpEndpoint = import.meta.env.VITE_APP_QUIZ_ENDPOINT;
    const quizWsEndpoint = import.meta.env.VITE_APP_QUIZ_ENDPOINT_WS;
    const quizWsHost = import.meta.env.VITE_APP_QUIZ_ENDPOINT_HOST;

    if (quizHttpEndpoint) {
      clients.push({
        key: 'quiz',
        httpEndpoint: quizHttpEndpoint,
        wsClientFactory: quizWsEndpoint && quizWsHost
          ? async () => {
              const currentUser = await authService.getCurrentUser();
              const tenantId = currentUser?.['custom:clientId'] || '';
              const wsClient = new AppSyncWebSocketClient(quizWsEndpoint, quizWsHost, authService, tenantId);
              await wsClient.start();
              return wsClient;
            }
          : undefined,
      });
    }

    // Add pgServer client for TaskList functionality
    const pgServerHttpEndpoint = import.meta.env.VITE_APP_PM_SERVER_ENDPOINT;
    if (pgServerHttpEndpoint) {
      clients.push({
        key: 'pgServer',
        httpEndpoint: pgServerHttpEndpoint,
        wsClientFactory: async () => {
          // Import dynamically to avoid circular dependencies
          return new GraphQLWSClient({
            url: import.meta.env.VITE_APP_PM_SERVER_ENDPOINT_WS,
            connectionParams: async () => {
              const token = await getCurrentUserIdToken(authService);
              if (!token) {
                throw new Error('no valid auth token');
              }
              return {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              };
            },
            retryAttempts: Infinity,
          });
        },
      });
    }

    return new DataClientService({
      defaultKey: 'main',
      clients,
      tableClientMap: {},
    });
  },
  createStorageService(config, tenantId: string) {
    const awsConfig = ensureAwsConfig(config);
    return new S3StorageService(awsConfig.storage, tenantId);
  },
  createEventService(config) {
    const awsConfig = ensureAwsConfig(config);
    return new SNSEventService(awsConfig.events);
  },
};

export default awsBackend;

function convertHttpToWss(httpUrl: string): string {
  const url = new URL(httpUrl);
  url.protocol = 'wss:';
  url.host = url.host.replace('appsync-api', 'appsync-realtime-api');
  return url.toString();
}
